<template>
  <!-- prettier-ignore -->
  <div>
    <label :class="classes">
      <div class="input__title">{{ autoTitle }}</div>

      <div class="input-element">
        <select
          ref="input"
          v-autofocus="maybeFocus"
          class="input__element"
          :name="$props.name"
          :disabled="$props.readonly"
          @focus="focused = true"
          @blur="focused = false"
          @change="emitUpdate">
          <option
            v-if="$props.modelValue === null"
            disabled
            selected="selected"
            :value="null">
            {{ $props.title }}
          </option>
          <option
            v-for="(option, index) in options"
            :key="index"
            :disabled="!!option.disabled"
            :selected="option.value === $props.modelValue"
            :value="option.value">
            <template v-if="option.value === $props.modelValue && !focused">
              {{ option.display ? option.display : option.text }}
            </template>

            <template v-else>
              {{ option.text }}
            </template>
          </option>
        </select>
        <the-icon
          art="light"
          name="angle-down"
          class="input__icon input__icon--fixed"
        />
      </div>

      <div class="input__hint">{{ autoHint }}</div>
    </label>
    <slot/>
  </div>
</template>

<script setup>
import VAutofocus from '@/directives/Focus'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBrowser } from '@/hooks'

const $emit = defineEmits(['update:modelValue'])

const props = defineProps({
  autofocus: {
    type: Boolean,
    default: true,
  },

  hint: {
    type: String,
    default: '',
  },

  isInteger: {
    type: Boolean,
    default: false,
  },

  name: {
    type: String,
    required: true,
  },

  options: {
    type: Array,
    required: true,
  },

  readonly: {
    type: Boolean,
    default: false,
  },

  required: {
    type: Boolean,
    default: false,
  },

  theme: {
    type: String,
    default: 'light',
  },

  title: {
    type: String,
    default: '',
  },

  validate: {
    type: Object,
    default: () => {},
  },

  modelValue: {
    type: [String, Number],
    default: null,
  },
})

// data
const focused = ref(false)

// hooks
const { tm } = useI18n()
const { browser } = useBrowser()

// computed
const active = computed(() => {
  return focused.value || props.modelValue !== null
})

const autoHint = computed(() => {
  const errors = Object.keys(props.validate?.$params ?? {}).filter(error => !props.validate[error])

  if (invalid.value && errors.length > 0) {
    return tm(`form.error.${errors[0]}`)
  } else {
    return props.hint
  }
})

const autoTitle = computed(() => {
  return `${props.title} ${isRequired.value ? '*' : ''}`
})

const classes = computed(() => {
  return {
    input: true,
    'input--focused': active.value,
    'input--invalid': invalid.value,
    'input--readonly': props.readonly,
    [`input--${props.theme}`]: true,
  }
})

const invalid = computed(() => {
  return props.validate?.$invalid && props.validate?.$error
})

const isRequired = computed(() => {
  return props.required || props.validate?.required !== undefined
})

//TODO remove autofocus if it's not working
const maybeFocus = computed(() => {
  return props.autofocus && browser.isDesktop
})

// methods: {
function emitUpdate(event) {
  let value = event.target.value

  if (props.isInteger) value = parseInt(value)

  props.validate?.$touch()
  $emit('update:modelValue', value)
}
</script>

<style name="mobile" scoped>
.input {
  font-family: Open Sans;
  display: inline-block;
  height: 45px;
  position: relative;
  width: 100%;
  pointer-events: none;

  &__title {
    position: absolute;
    font-size: 15px;
    line-height: 16px;
    color: #999999;
    padding: 4px 10px 0 10px;
    top: 15px;
    transition: top 0.2s ease-in-out, font-size 0.2s ease-in-out;
    z-index: 1;
    width: 100%;
    cursor: pointer;
  }

  &-element {
    width: 100%;
    padding-top: 19px;
    border-bottom: 1px solid var(--c-black);
    cursor: pointer;

    transition: border-bottom-color 0.2s ease-in-out;
  }

  &__element {
    font-family: 'Open Sans';
    background: none;
    border: none;
    outline: none;
    color: var(--c-black);
    font-size: 16px;
    line-height: 20px;
    height: 26px;
    padding: 0 10px 2px 7px;
    width: 100%;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
    pointer-events: auto;
    appearance: none;
  }

  .input__icon {
    position: absolute;
    color: var(--c-black);
    right: 10px;
    font-size: 15px;
    margin: 5px 0 0 0;
  }

  &__hint {
    font-size: 15px;
    line-height: 20px;
    color: #999999;
    padding: 0 0 0 10px;

    transition: color 0.2s ease-in-out;
  }

  &--focused {
    .input__title {
      font-size: 12px;
      top: 0;
    }

    .input__element {
      opacity: 1;
    }
  }

  &--invalid {
    .input-element {
      border-bottom-color: var(--c-error);
    }

    .input__title,
    .input__hint {
      color: var(--c-error);
    }
  }

  &--dark {
    .input-element {
      background-color: var(--c-warm-gray);
    }
  }

  &--light {
    .input-element {
      background-color: var(--c-white);
    }
  }

  &--readonly {
    .input {
      &-element {
        border: none;
      }

      &__element {
        pointer-events: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        padding-left: 10px;
        color: #999999;
      }

      &__hint {
        visibility: hidden;
      }
    }
  }
}

:deep(.input__icon:not(.input__icon--fixed)) {
  position: absolute;
  font-size: 20px;
  line-height: 50px;
  background-color: transparent;
  padding: 15px 0;
  right: 15px;
  color: var(--c-original-green);
  outline: none;
  z-index: 1;
  cursor: pointer;
}
</style>
